
import React, { useState, useRef } from 'react';
import { Camera, Loader2, AlertCircle, Key, RefreshCw, Image as ImageIcon } from 'lucide-react';
import { parseScheduleImage } from '../services/geminiService';

interface Props {
  onDataParsed: (data: any) => void;
}

// Define a union type for specific error states to handle them distinctly
type ImageUploadError = 'QUOTA_EXCEEDED' | 'API_KEY_MISSING' | 'GENERIC_ERROR' | null;

export const ImageUpload: React.FC<Props> = ({ onDataParsed }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ImageUploadError>(null);
  const [errorMessageText, setErrorMessageText] = useState<string | null>(null); // For custom text
  const [lastBase64, setLastBase64] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Helper function to resize and compress image
  const resizeImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.src = URL.createObjectURL(file);
      
      img.onload = () => {
        const maxWidth = 1080; // Optimized width for OCR
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            reject(new Error("Could not get canvas context"));
            return;
        }

        // Draw image on canvas
        ctx.drawImage(img, 0, 0, width, height);

        // Compress to JPEG with 0.8 quality
        // This drastically reduces payload size for the AI API
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        
        // Clean up memory
        URL.revokeObjectURL(img.src);
        
        // Return only the Base64 data part
        resolve(dataUrl.split(',')[1]);
      };

      img.onerror = (err) => {
        URL.revokeObjectURL(img.src);
        reject(err);
      };
    });
  };

  const processImage = async (base64Data: string) => {
    setLoading(true);
    setError(null);
    setErrorMessageText(null);
    try {
      const result = await parseScheduleImage(base64Data);
      onDataParsed(result);
    } catch (err: any) {
       console.error("ImageUpload - Error parsing image:", err); // Log the full error
       if (err.message === 'QUOTA_EXCEEDED') {
         setError('QUOTA_EXCEEDED');
         setErrorMessageText("Cota da API excedida. Por favor, use sua própria chave.");
       } else if (err.message === "API Key is missing. Please select a key.") {
         setError('API_KEY_MISSING');
         setErrorMessageText("Chave de API ausente. Por favor, selecione uma chave válida.");
       }
       else {
         setError('GENERIC_ERROR');
         setErrorMessageText("Não foi possível ler a escala. Tente uma foto mais clara.");
       }
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Basic validation
    if (!file.type.startsWith('image/')) {
        setError('GENERIC_ERROR');
        setErrorMessageText("Por favor, selecione um arquivo de imagem válido.");
        return;
    }

    setLoading(true); // Start loading immediately while compressing

    try {
        // Optimize image before sending using resizeImage
        const optimizedBase64 = await resizeImage(file);
        setLastBase64(optimizedBase64); // Save for retry
        await processImage(optimizedBase64);
    } catch (err) {
        console.error("Compression error:", err);
        setError('GENERIC_ERROR');
        setErrorMessageText("Erro ao processar a imagem. Tente outra foto.");
        setLoading(false);
    }
  };

  const triggerUpload = () => {
    fileInputRef.current?.click();
  };

  const handleRetry = async () => {
    if (lastBase64) {
        await processImage(lastBase64);
    }
  };

  const openKeySelector = async (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent file input from opening when clicking the key icon
    if ((window as any).aistudio) {
        try {
            await (window as any).aistudio.openSelectKey();
            setError(null); // Clear error visually
            setErrorMessageText(null);
            // Auto retry if we have the image
            if (lastBase64) {
                setTimeout(() => handleRetry(), 500);
            }
        } catch (e) {
            console.error("Failed to open key selector", e);
            setError('GENERIC_ERROR');
            setErrorMessageText("Não foi possível abrir o seletor de chaves.");
        }
    } else {
        setError('GENERIC_ERROR');
        setErrorMessageText("Seletor de chave não disponível neste ambiente.");
    }
  };

  const shouldShowApiKeyPrompt = error === 'QUOTA_EXCEEDED' || error === 'API_KEY_MISSING';

  return (
    <div className="max-w-md mx-auto text-center">
      <div 
        onClick={loading ? undefined : triggerUpload}
        className={`border-2 border-dashed rounded-xl p-10 transition-all group relative overflow-hidden ${
            error ? 'border-red-500 bg-red-900/10' : 'border-slate-600 bg-slate-800/50 hover:bg-slate-800 hover:border-orange-500 cursor-pointer'
        }`}
      >
        <input 
          type="file" 
          ref={fileInputRef} 
          className="hidden" 
          accept="image/*" 
          // Removed capture="environment" to allow gallery selection on mobile
          onChange={handleFileChange}
          disabled={loading}
        />
        
        <div className="absolute top-3 right-3 z-10">
           <button 
             onClick={openKeySelector}
             className="p-2 rounded-full bg-slate-700/50 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
             title="Trocar Chave API"
           >
             <Key className="w-4 h-4" />
           </button>
        </div>

        {loading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="w-12 h-12 text-orange-500 animate-spin mb-4" />
            <p className="text-slate-300 font-medium">Otimizando e analisando imagem...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className={`p-4 rounded-full mb-4 transition-colors flex items-center gap-2 ${error ? 'bg-red-900/30' : 'bg-slate-700 group-hover:bg-slate-600'}`}>
              {error ? (
                <AlertCircle className="w-8 h-8 text-red-500" />
              ) : (
                <>
                  <Camera className="w-6 h-6 text-orange-500" />
                  <span className="text-slate-500">|</span>
                  <ImageIcon className="w-6 h-6 text-blue-400" />
                </>
              )}
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">{error ? 'Erro na Leitura' : 'Enviar Escala'}</h3>
            <p className="text-sm text-slate-400 max-w-xs mx-auto">
              {errorMessageText || "Tire uma foto ou envie um print do app Deputy."}
            </p>
          </div>
        )}
      </div>

      {shouldShowApiKeyPrompt && (
        <div className="mt-4 flex flex-col gap-3 animate-fade-in">
             <button 
                onClick={openKeySelector}
                className="w-full flex items-center justify-center gap-2 bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-3 rounded-lg text-sm font-bold transition-colors shadow-lg"
            >
                <Key className="w-4 h-4" />
                Trocar Chave API (Resolver Problema de Cota)
            </button>
            
            {lastBase64 && (
                <button 
                    onClick={handleRetry}
                    className="w-full flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-white px-4 py-3 rounded-lg text-sm font-medium transition-colors border border-slate-600"
                >
                    <RefreshCw className="w-4 h-4" />
                    Tentar Novamente
                </button>
            )}
        </div>
      )}
    </div>
  );
};
